import { useState, useEffect, useRef } from 'react';
import { useScripts, useSongs, useArtists } from '../hooks/useFirestore';
import { useAuth } from '../lib/AuthContext';
import { Modal } from './Tasks';
import { Sparkles, Send, Check, Edit3 } from 'lucide-react';
import { db } from '../lib/firebase';
import { doc, onSnapshot, setDoc, serverTimestamp, collection, addDoc, updateDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import MoodBoard from '../components/MoodBoard';

// ── Content Pillars ───────────────────────────────────────────
const PILLARS = {
  ahzira: [
    { id: 'transmissions', name: 'Transmissions', emoji: '📡', style: 'Narrative',    desc: 'World-building content. Cinematic, rare, committed. These live in the universe of the music.', color: '#c2873a' },
    { id: 'craft',         name: 'The Craft',     emoji: '🔊', style: 'Process',      desc: 'The technical obsession. Production process, sound design, the work behind the music.', color: '#7b4fc4' },
    { id: 'character',     name: 'The Character', emoji: '🜃', style: 'Identity',     desc: 'Fully embodied. Visual identity, presence, the look and feel.', color: '#e8621a' },
    { id: 'music',         name: 'The Music',     emoji: '✦',  style: 'Release',      desc: 'Song-specific content. Drop moments, lyric reveals, release energy.', color: '#1a9080' },
  ],
  avidx: [
    { id: 'confessionals', name: 'Confessionals',    emoji: '🎙', style: 'Identity',    desc: 'Self-aware emotional honesty. The reason people follow you, not just the music.', color: '#7b4fc4' },
    { id: 'process',       name: 'The Process',      emoji: '🎛', style: 'Process',     desc: 'Writing, recording, the beautiful mess. Behind-the-song energy.', color: '#1a9080' },
    { id: 'chaos',         name: 'Controlled Chaos', emoji: '⚡', style: 'Upper Funnel', desc: 'Humor, personality, hooks that stop the scroll. Where discovery happens.', color: '#e8621a' },
    { id: 'music',         name: 'The Music',        emoji: '🎵', style: 'Release',     desc: 'Song-specific content. Emotional hooks, captions, the actual art.', color: '#c98a18' },
  ],
};

// ── Artist Skin Configs ───────────────────────────────────────
const SKINS = {
  ahzira: {
    bg:          'var(--deep, #080810)',
    surface:     'var(--surface, #0f0f1a)',
    surface2:    'var(--surface2, #141420)',
    surface3:    'var(--surface3, #1a1a28)',
    border:      'rgba(194,135,58,.22)',
    borderActive:'rgba(194,135,58,.55)',
    accent:      '#c2873a',
    accent2:     '#e8621a',
    dim:         'var(--dim, #3a3a5a)',
    muted:       '#7a6545',
    text:        'var(--text, #e8e8f0)',
    glow:        'rgba(194,135,58,.06)',
    titleFont:   '"Palatino Linotype", "Book Antiqua", Palatino, serif',
    monoFont:    'var(--mono, "Courier New", monospace)',
    label:       'AhziRa',
    tagline:     'Content Command Center',
    tabAccent:   '#c2873a',
  },
  avidx: {
    bg:         '#070b14',
    surface:    '#0d1220',
    surface2:   '#131a2e',
    surface3:   '#1a2240',
    border:     'rgba(123,79,196,.2)',
    borderActive:'rgba(123,79,196,.6)',
    accent:     '#7b4fc4',
    accent2:    '#e8621a',
    dim:        '#2a3050',
    muted:      '#6070a0',
    text:       '#e8eeff',
    glow:       'rgba(123,79,196,.08)',
    titleFont:  '"Courier New", Courier, monospace',
    monoFont:   '"Courier New", Courier, monospace',
    label:      'Avidx',
    tagline:    'Content Command Center',
    tabAccent:  '#7b4fc4',
  },
};

// ── Firestore hook for content data ──────────────────────────
function useContentData(artistId) {
  const [data, setData] = useState(null);
  const docId = artistId || 'avidx';
  const ref = doc(db, 'content', docId);
  useEffect(() => {
    return onSnapshot(ref, snap => {
      setData(snap.exists() ? snap.data() : { identity: [], pillars: PILLARS[docId] || PILLARS.avidx });
    });
  }, [docId]);
  const update = (updates) => setDoc(ref, updates, { merge: true });
  return { data, update };
}

// ── Decorative background per skin ───────────────────────────
function SkinBackground({ skin }) {
  if (skin === 'ahzira') return (
    <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, overflow:'hidden' }}>
      {/* Desert horizon gradient */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'40%', background:'linear-gradient(to top, rgba(194,135,58,.04), transparent)' }}/>
      {/* Subtle sand texture dots */}
      {[...Array(30)].map((_, i) => (
        <div key={i} style={{ position:'absolute', width: 1+Math.random()*2, height: 1+Math.random()*2, borderRadius:'50%', background:'rgba(194,135,58,.15)', left:`${Math.random()*100}%`, top:`${Math.random()*100}%` }}/>
      ))}
    </div>
  );
  // Avidx — VHS scanlines + neon glow
  return (
    <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, overflow:'hidden' }}>
      <div style={{ position:'absolute', inset:0, backgroundImage:'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(123,79,196,.015) 2px, rgba(123,79,196,.015) 4px)' }}/>
      <div style={{ position:'absolute', top:'20%', left:'-10%', width:'50%', height:'50%', background:'radial-gradient(ellipse, rgba(123,79,196,.06) 0%, transparent 70%)', filter:'blur(40px)' }}/>
      <div style={{ position:'absolute', bottom:'10%', right:'-5%', width:'40%', height:'40%', background:'radial-gradient(ellipse, rgba(232,98,26,.04) 0%, transparent 70%)', filter:'blur(60px)' }}/>
    </div>
  );
}

// ── Tab I: Identity ───────────────────────────────────────────
function IdentityTab({ data, update, s, artistId }) {
  const canvas = data?.identity || [];
  return (
    <div style={{ padding:'28px 30px' }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>
          Tab I · Brand DNA
        </div>
        <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont, marginBottom:4 }}>
          Identity Canvas
        </div>
        <div style={{ fontSize:12, color:s.muted, fontFamily:s.monoFont, lineHeight:1.7 }}>
          Drop images, references, phrases, and notes that define the world every piece of content should live inside.
        </div>
      </div>

      {/* Moodboard */}
      <div style={{ borderRadius:16, overflow:'hidden', border:`1px solid ${s.border}`, background:s.surface }}>
        <div style={{ padding:'14px 18px', borderBottom:`1px solid ${s.border}`, display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:s.accent }}/>
          <span style={{ fontSize:11, fontWeight:700, color:s.muted, fontFamily:s.monoFont, letterSpacing:1, textTransform:'uppercase' }}>Canvas</span>
        </div>
        <MoodBoard
          items={canvas}
          onChange={items => update({ identity: items })}
          showSteps={false}
          fillPage
          minHeight={400}
        />
      </div>
    </div>
  );
}

// ── Tab II: Pillars ───────────────────────────────────────────
const IDEA_STATUSES = [
  { id:'idea',      label:'Idea',      color:'var(--dim)' },
  { id:'filming',   label:'To Film',   color:'#c98a18' },
  { id:'filmed',    label:'Filmed',    color:'#7b4fc4' },
  { id:'editing',   label:'Editing',   color:'#1a9080' },
  { id:'scheduled', label:'Scheduled', color:'#e8621a' },
  { id:'posted',    label:'Posted',    color:'#27ae60' },
];

function PillarsTab({ data, update, s, artistId }) {
  const pillars        = data?.pillars || PILLARS[artistId] || PILLARS.avidx;
  const ideas          = data?.ideas   || {};
  const STYLES         = ['Narrative','Process','Identity','Upper Funnel','Release','Engagement'];
  const [editingPillar, setEditingPillar] = useState(null);
  const [pillarForm,    setPillarForm]    = useState({});
  const [addingIdea,    setAddingIdea]    = useState(null);
  const [ideaForm,      setIdeaForm]      = useState({ hook:'', caption:'', format:'', captureType:'', songLink:'', status:'idea' });
  const [expandedIdea,  setExpandedIdea]  = useState(null);
  const [addingLink,    setAddingLink]    = useState(null);
  const [linkUrl,       setLinkUrl]       = useState('');
  const [showUnsorted,  setShowUnsorted]  = useState(false);
  const [dragItem,      setDragItem]      = useState(null); // { ideaId, fromPillarId }

  const FORMATS      = ['Reel','Story','Carousel'];
  const CAPTURE_TYPES = [
    { id:'camera', label:'To Camera', desc:'You + a surface + decent light' },
    { id:'catch',  label:'Catch It',  desc:'Keep your phone ready, film when it happens' },
  ];

  const getDomain = (url) => { try { return new URL(url).hostname.replace('www.',''); } catch { return url; } };

  const handleDrop = (toPillarId) => {
    if (!dragItem || dragItem.fromPillarId === toPillarId) return;
    const fromIdeas  = (ideas[dragItem.fromPillarId] || []);
    const idea       = fromIdeas.find(i => i.id === dragItem.ideaId);
    if (!idea) return;
    const updated    = { ...ideas,
      [dragItem.fromPillarId]: fromIdeas.filter(i => i.id !== dragItem.ideaId),
      [toPillarId]: [...(ideas[toPillarId] || []), idea],
    };
    update({ ideas: updated });
    setDragItem(null);
  };

  const moveIdeaWithinPillar = (pillarId, fromIdx, toIdx) => {
    const list   = [...(ideas[pillarId] || [])];
    const [item] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, item);
    update({ ideas: { ...ideas, [pillarId]: list } });
  };

  const savePillar = () => {
    update({ pillars: pillars.map(p => p.id === editingPillar ? { ...p, ...pillarForm } : p) });
    setEditingPillar(null);
  };
  const saveIdea = (pillarId) => {
    if (!ideaForm.hook.trim() && !ideaForm.caption.trim()) return;
    const existing = ideas[pillarId] || [];
    update({ ideas: { ...ideas, [pillarId]: [...existing, { id: Date.now().toString(), links:[], ...ideaForm }] } });
    setAddingIdea(null);
    setIdeaForm({ hook:'', caption:'', format:'', captureType:'', songLink:'', status:'idea' });
  };
  const updateIdea = (pillarId, ideaId, changes) => {
    update({ ideas: { ...ideas, [pillarId]: (ideas[pillarId]||[]).map(i => i.id===ideaId ? {...i,...changes} : i) } });
  };
  const deleteIdea = (pillarId, ideaId) => {
    update({ ideas: { ...ideas, [pillarId]: (ideas[pillarId]||[]).filter(i => i.id!==ideaId) } });
  };
  const addLink = (pillarId, ideaId, ideaLinks) => {
    if (!linkUrl.trim()) return;
    updateIdea(pillarId, ideaId, { links: [...ideaLinks, { id: Date.now().toString(), url: linkUrl.trim() }] });
    setLinkUrl(''); setAddingLink(null);
  };

  // Single unified card — same UI for new and existing ideas
  const renderIdeaCard = (idea, pillarId, pillar, isNew = false) => {
    const st         = IDEA_STATUSES.find(x => x.id === (idea.status||'idea')) || IDEA_STATUSES[0];
    const ideaLinks  = idea.links || [];
    const isExpanded = expandedIdea === idea.id;
    const accentColor = pillar?.color || s.accent;

    const setField = (field, value) => {
      if (isNew) {
        setIdeaForm(f => ({ ...f, [field]: value }));
      } else {
        updateIdea(pillarId, idea.id, { [field]: value });
      }
    };

    const val = (field) => isNew ? ideaForm[field] : idea[field];

    return (
      <div key={idea.id}
        draggable={!isNew}
        onDragStart={() => !isNew && setDragItem({ ideaId: idea.id, fromPillarId: pillarId })}
        onDragEnd={() => setDragItem(null)}
        style={{ borderRadius:10, background:s.surface2, border:`1px solid ${isExpanded||isNew ? accentColor+'66' : s.border}`, transition:'border-color .15s', overflow:'hidden', cursor: isNew ? 'default' : 'grab', opacity: dragItem?.ideaId === idea.id ? 0.5 : 1 }}>
        {/* Always-visible summary / click to expand */}
        <div onClick={() => !isNew && setExpandedIdea(isExpanded ? null : idea.id)}
          style={{ padding:'10px 12px', cursor: isNew ? 'default' : 'pointer' }}>
          {/* Hook field */}
          <input
            value={val('hook')||''}
            onChange={e => setField('hook', e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Hook (OSC) — the line that stops the scroll..."
            style={{ width:'100%', marginBottom:6, fontSize:12, fontWeight:600, background:'transparent', border:'none', borderBottom:`1px solid ${s.border}`, color:s.text, padding:'2px 0', outline:'none', borderRadius:0 }}
          />
          {/* Caption field */}
          <textarea
            value={val('caption')||''}
            onChange={e => setField('caption', e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Caption (OfSC) — the context, the emotion, the CTA..."
            rows={2}
            style={{ width:'100%', marginBottom:8, fontSize:11, background:'transparent', border:'none', color:s.muted, padding:'2px 0', outline:'none', resize:'none', lineHeight:1.6, fontStyle:'italic', borderBottom:`1px solid ${s.border}` }}
          />
          {/* Status + badges row */}
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <select value={val('status')||'idea'} onChange={e => { e.stopPropagation(); setField('status', e.target.value); }} onClick={e => e.stopPropagation()}
              style={{ fontSize:9, padding:'2px 6px', borderRadius:10, background:`${st.color}18`, border:`1px solid ${st.color}44`, color:st.color, fontFamily:s.monoFont, fontWeight:700, cursor:'pointer' }}>
              {IDEA_STATUSES.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
            {val('format') && <div style={{ fontSize:9, padding:'2px 6px', borderRadius:10, background:`${accentColor}18`, color:accentColor, fontFamily:s.monoFont, fontWeight:700 }}>{val('format')}</div>}
            {val('captureType') && <div style={{ fontSize:9, color:s.dim, fontFamily:s.monoFont }}>{val('captureType')==='camera' ? '📷' : '📱'}</div>}
            {!isNew && <div style={{ marginLeft:'auto', fontSize:10, color:s.dim }}>{isExpanded ? '▲' : '▼'}</div>}
          </div>
        </div>

        {/* Expanded / always open for new */}
        {(isExpanded || isNew) && (
          <div style={{ padding:'10px 12px', borderTop:`1px solid ${s.border}`, background:s.surface3 }}
            onClick={e => e.stopPropagation()}>

            {/* Move to pillar (unsorted only) */}
            {pillarId === 'unsorted' && !isNew && (
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:4 }}>Move to Pillar</label>
                <select defaultValue="" onChange={e => {
                  if (!e.target.value) return;
                  const tp = e.target.value;
                  update({ ideas: { ...ideas, unsorted:(ideas['unsorted']||[]).filter(i=>i.id!==idea.id), [tp]:[...(ideas[tp]||[]),idea] } });
                  setExpandedIdea(null);
                }} style={{ width:'100%', background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:12 }}>
                  <option value="">Select pillar...</option>
                  {pillars.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>)}
                </select>
              </div>
            )}

            {/* Format + Capture */}
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:4 }}>Format</label>
                <select value={val('format')||''} onChange={e => setField('format', e.target.value)}
                  style={{ width:'100%', background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:11 }}>
                  <option value="">Not decided</option>
                  {FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:4 }}>Capture</label>
                <select value={val('captureType')||''} onChange={e => setField('captureType', e.target.value)}
                  style={{ width:'100%', background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:11 }}>
                  <option value="">Not decided</option>
                  {CAPTURE_TYPES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Song link */}
            <input value={val('songLink')||''} onChange={e => setField('songLink', e.target.value)}
              placeholder="🎵 Linked song (optional)..."
              style={{ width:'100%', marginBottom:10, fontSize:11, background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, padding:'5px 10px' }}/>

            {/* Inspo links (existing only) */}
            {!isNew && ideaLinks.map(link => (
              <div key={link.id} style={{ display:'flex', alignItems:'center', gap:4, marginBottom:4 }}>
                <a href={link.url} target="_blank" rel="noreferrer"
                  style={{ flex:1, fontSize:9, color:accentColor, fontFamily:s.monoFont, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textDecoration:'none' }}>
                  ↗ {getDomain(link.url)}
                </a>
                <button onClick={() => updateIdea(pillarId, idea.id, { links: ideaLinks.filter(l => l.id !== link.id) })}
                  style={{ background:'none', border:'none', cursor:'pointer', color:s.dim, fontSize:9, padding:0 }}>✕</button>
              </div>
            ))}
            {!isNew && (addingLink === idea.id ? (
              <div style={{ display:'flex', gap:4, marginBottom:8 }}>
                <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." autoFocus
                  onKeyDown={e => { if(e.key==='Enter') addLink(pillarId,idea.id,ideaLinks); if(e.key==='Escape') setAddingLink(null); }}
                  style={{ flex:1, fontSize:10, background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:5, padding:'3px 7px' }}/>
                <button onClick={() => addLink(pillarId,idea.id,ideaLinks)} className="btn btn-primary btn-sm" style={{ fontSize:9 }}>+</button>
                <button onClick={() => setAddingLink(null)} className="btn btn-ghost btn-sm" style={{ fontSize:9 }}>✕</button>
              </div>
            ) : (
              <button onClick={() => { setAddingLink(idea.id); setLinkUrl(''); }}
                style={{ fontSize:9, color:s.dim, background:'none', border:'none', cursor:'pointer', padding:'0 0 8px', fontFamily:s.monoFont }}>
                + inspo link
              </button>
            ))}

            {/* Actions */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:4 }}>
              {isNew ? (
                <>
                  <button onClick={() => { setAddingIdea(null); setIdeaForm({hook:'',caption:'',format:'',captureType:'',songLink:'',status:'idea'}); }} className="btn btn-ghost btn-sm">Cancel</button>
                  <button onClick={() => saveIdea(pillarId)} disabled={!ideaForm.hook.trim()&&!ideaForm.caption.trim()} className="btn btn-primary btn-sm">Add</button>
                </>
              ) : (
                <button onClick={() => deleteIdea(pillarId, idea.id)}
                  style={{ fontSize:9, color:'#e74c3c', background:'none', border:'none', cursor:'pointer', fontFamily:s.monoFont, marginLeft:'auto' }}>
                  Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // + Add idea button / new card
  const renderAddButton = (pillarId, pillar) => (
    addingIdea === pillarId
      ? renderIdeaCard({ id:'new-'+pillarId, ...ideaForm }, pillarId, pillar, true)
      : (
        <button onClick={() => { setAddingIdea(pillarId); setIdeaForm({hook:'',caption:'',format:'',captureType:'',songLink:'',status:'idea'}); }}
          style={{ fontSize:11, color:s.muted, background:'none', border:`1px dashed ${s.border}`, borderRadius:8, cursor:'pointer', padding:'8px', width:'100%', fontFamily:s.monoFont, transition:'border-color .15s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = pillar?.color||s.accent}
          onMouseLeave={e => e.currentTarget.style.borderColor = s.border}>
          + Add idea
        </button>
      )
  );

  const unsortedIdeas = ideas['unsorted'] || [];

  return (
    <div style={{ padding:'28px 30px' }}>
      {/* Header row */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: showUnsorted ? 12 : 20 }}>
        <div>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>Tab II · Content Pillars</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont }}>The Four Pillars</div>
        </div>
        <button onClick={() => setShowUnsorted(v=>!v)}
          style={{ padding:'6px 14px', borderRadius:8, background: showUnsorted ? `${s.accent}18` : 'none', border:`1px solid ${showUnsorted ? s.accent : s.border}`, color: showUnsorted ? s.accent : s.muted, cursor:'pointer', fontSize:11, fontFamily:s.monoFont, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
          {unsortedIdeas.length > 0 && <span style={{ background:s.accent, color:'#fff', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800 }}>{unsortedIdeas.length}</span>}
          Unsorted {showUnsorted ? '▲' : '▼'}
        </button>
      </div>

      {/* Unsorted tray */}
      {showUnsorted && (
        <div style={{ marginBottom:20, padding:'16px 20px', borderRadius:14, background:s.surface, border:`1px solid ${s.border}` }}>
          <div style={{ fontSize:10, letterSpacing:2, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:12 }}>
            Unsorted Ideas — assign each one to a pillar to move it
          </div>
          {unsortedIdeas.length === 0 ? (
            <div onDragOver={e => e.preventDefault()} onDrop={() => handleDrop('unsorted')}
              style={{ fontSize:12, color:s.dim, fontFamily:s.monoFont, padding:'20px 0', textAlign:'center', border:`2px dashed ${dragItem ? s.accent : 'transparent'}`, borderRadius:10, transition:'border-color .15s' }}>
              {dragItem ? 'Drop here to unsort' : 'No unsorted ideas.'}
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
              {unsortedIdeas.map(idea => renderIdeaCard(idea, 'unsorted', null))}
            </div>
          )}
          <div style={{ marginTop:12 }}>
            {renderAddButton('unsorted', null)}
          </div>
        </div>
      )}

      {/* Four pillar columns */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, alignItems:'start' }}>
        {pillars.map(pillar => {
          const pillarIdeas = ideas[pillar.id] || [];
          const counts = IDEA_STATUSES.reduce((acc,st) => ({...acc,[st.id]:pillarIdeas.filter(i=>i.status===st.id).length}),{});
          return (
            <div key={pillar.id} style={{ borderRadius:14, background:s.surface, border:`1px solid ${s.border}`, overflow:'hidden' }}>
              {/* Column header */}
              <div style={{ padding:'14px 16px', borderBottom:`1px solid ${s.border}`, background:`${pillar.color}08`, position:'relative' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${pillar.color},${pillar.color}44)` }}/>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <span style={{ fontSize:16 }}>{pillar.emoji}</span>
                  <div style={{ fontSize:13, fontWeight:800, color:s.text, flex:1 }}>{pillar.name}</div>
                  <button onClick={() => { setEditingPillar(pillar.id); setPillarForm({name:pillar.name,style:pillar.style,desc:pillar.desc}); }}
                    style={{ background:'none', border:'none', cursor:'pointer', color:s.dim, fontSize:10 }}>✎</button>
                </div>
                <div style={{ fontSize:9, padding:'2px 8px', borderRadius:20, background:`${pillar.color}18`, color:pillar.color, fontFamily:s.monoFont, fontWeight:700, display:'inline-block', marginBottom:8 }}>
                  {pillar.style}
                </div>
                <div style={{ fontSize:11, color:s.muted, lineHeight:1.6, fontFamily:s.monoFont }}>{pillar.desc}</div>
                <div style={{ display:'flex', gap:4, marginTop:8, flexWrap:'wrap' }}>
                  {IDEA_STATUSES.filter(st=>counts[st.id]>0).map(st=>(
                    <div key={st.id} style={{ fontSize:8, padding:'1px 5px', borderRadius:8, background:`${st.color}18`, color:st.color, fontFamily:s.monoFont, fontWeight:700 }}>
                      {counts[st.id]} {st.label}
                    </div>
                  ))}
                </div>
              </div>
              {/* Pillar editor */}
              {editingPillar === pillar.id && (
                <div style={{ padding:'12px 14px', borderBottom:`1px solid ${s.border}`, background:s.surface2 }}>
                  <div className="form-group" style={{ margin:'0 0 8px' }}>
                    <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Name</label>
                    <input value={pillarForm.name} onChange={e=>setPillarForm(f=>({...f,name:e.target.value}))}
                      style={{ background:s.surface3, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:12 }}/>
                  </div>
                  <div className="form-group" style={{ margin:'0 0 8px' }}>
                    <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Style</label>
                    <select value={pillarForm.style} onChange={e=>setPillarForm(f=>({...f,style:e.target.value}))}
                      style={{ background:s.surface3, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:12 }}>
                      {STYLES.map(st=><option key={st}>{st}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin:'0 0 10px' }}>
                    <label style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Description</label>
                    <textarea rows={2} value={pillarForm.desc} onChange={e=>setPillarForm(f=>({...f,desc:e.target.value}))}
                      style={{ background:s.surface3, border:`1px solid ${s.border}`, color:s.text, borderRadius:6, fontSize:11, lineHeight:1.6 }}/>
                  </div>
                  <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                    <button onClick={()=>setEditingPillar(null)} className="btn btn-ghost btn-sm">Cancel</button>
                    <button onClick={savePillar} className="btn btn-primary btn-sm">Save</button>
                  </div>
                </div>
              )}
              {/* Ideas */}
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(pillar.id)}
                style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:8, minHeight:60, transition:'background .15s', background: dragItem && dragItem.fromPillarId !== pillar.id ? `${pillar.color}08` : 'transparent' }}>
                {pillarIdeas.map(idea => renderIdeaCard(idea, pillar.id, pillar))}
                {renderAddButton(pillar.id, pillar)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AI Spark prompts per pillar style ────────────────────────
const SPARK_PROMPTS = {
  Narrative:     ['Film a 10-second scene with no context. Let people wonder.', 'One static shot, perfect lighting. No speaking required.', 'Shoot a location that feels like your music sounds.'],
  Process:       ['Open voice memo, record yourself talking through an idea. That\'s the content.', 'Film your workspace right now, exactly as it is.', 'Capture 3 seconds of the actual work. No setup needed.'],
  Identity:      ['Film yourself doing something completely ordinary. Your presence is the content.', 'One outfit. One mirror. Thirty seconds.', 'Film the thing you almost didn\'t post.'],
  'Upper Funnel':['Write a hook about something you\'ve never told anyone about your music.', 'What\'s the most relatable thing about making this song? Say that.', 'Film the 5 second moment before you hit record.'],
  Release:       ['Pull one line from your current song. Film yourself saying it like it\'s a secret.', 'Show the artwork with no caption. Let the comments come.', 'Post a 15 second snippet. No context. Just the drop.'],
  Engagement:    ['Ask one genuine question you actually want answered.', 'Share an opinion about music you\'re ready to defend.', 'Post something that starts with "I never talk about this but..."'],
};

function getSparkPrompt(style) {
  const prompts = SPARK_PROMPTS[style] || SPARK_PROMPTS['Identity'];
  return prompts[Math.floor(Math.random() * prompts.length)];
}

// ── Tab III: Spark ────────────────────────────────────────────
function SparkTab({ data, update, s, artistId, onCreateTask }) {
  const pillars   = data?.pillars || PILLARS[artistId] || PILLARS.avidx;
  const ideas     = data?.ideas   || {};
  const streak    = data?.streak  || { count:0, lastDate:null };
  const [pair,    setPair]    = useState(null);
  const [chosen,  setChosen]  = useState(null);
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const isStreakToday = streak.lastDate === today;

  // Find oldest untouched idea from each pillar
  const getIdeasByPillar = () => {
    const pool = [];
    for (const p of pillars) {
      const pillarIdeas = (ideas[p.id] || []).filter(i => i.status === 'idea');
      if (pillarIdeas.length > 0) pool.push({ ...pillarIdeas[0], pillar: p });
    }
    return pool;
  };

  const generatePair = () => {
    setChosen(null);
    const pool = getIdeasByPillar();
    // Pick 2 random pillars — one idea from each, or an AI prompt
    const shuffled = [...pillars].sort(() => Math.random() - 0.5);
    const p1 = shuffled[0];
    const p2 = shuffled[1];
    const idea1 = (ideas[p1.id] || []).find(i => i.status === 'idea');
    const idea2 = (ideas[p2.id] || []).find(i => i.status === 'idea');
    setPair([
      { type: idea1 ? 'idea' : 'prompt', pillar: p1, idea: idea1, prompt: getSparkPrompt(p1.style) },
      { type: idea2 ? 'idea' : 'prompt', pillar: p2, idea: idea2, prompt: getSparkPrompt(p2.style) },
    ]);
  };

  useEffect(() => { generatePair(); }, [artistId]);

  const choose = (card) => {
    setChosen(card);
    // Move idea to filming if it's from Tab II
    if (card.type === 'idea' && card.idea) {
      const pillarIdeas = ideas[card.pillar.id] || [];
      const updated = pillarIdeas.map(i => i.id === card.idea.id ? { ...i, status: 'filming' } : i);
      const newStreak = isStreakToday ? streak : { count: streak.count + 1, lastDate: today };
      update({ ideas: { ...ideas, [card.pillar.id]: updated }, streak: newStreak });
    } else {
      const newStreak = isStreakToday ? streak : { count: streak.count + 1, lastDate: today };
      update({ streak: newStreak });
    }
  };

  const makeTask = (card) => {
    const title = card.type === 'idea' ? card.idea?.title : card.prompt;
    onCreateTask({ title: `[Content] ${title}`, category: 'content', priority: 'P1', artistId });
  };

  return (
    <div style={{ padding:'28px 30px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:28 }}>
        <div>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>Tab III · Daily Spark</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont }}>This or That</div>
          <div style={{ fontSize:12, color:s.muted, fontFamily:s.monoFont, marginTop:4 }}>Pick one. Film it. Don't overthink it.</div>
        </div>
        {/* Streak */}
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:28, fontWeight:900, color:s.accent, fontFamily:s.monoFont, lineHeight:1 }}>{streak.count}</div>
          <div style={{ fontSize:9, letterSpacing:2, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont }}>Day Streak</div>
          {isStreakToday && <div style={{ fontSize:9, color:'#27ae60', fontFamily:s.monoFont, marginTop:2 }}>✓ Done today</div>}
        </div>
      </div>

      {/* This or That cards */}
      {pair && !chosen && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
          {pair.map((card, i) => (
            <div key={i} onClick={() => choose(card)}
              style={{ padding:'28px 24px', borderRadius:18, background:s.surface, border:`2px solid ${s.border}`, cursor:'pointer', transition:'all .2s', position:'relative', overflow:'hidden' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = card.pillar.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = s.border; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:4, background:`linear-gradient(90deg,${card.pillar.color},${card.pillar.color}44)` }}/>
              <div style={{ fontSize:10, padding:'2px 10px', borderRadius:20, background:`${card.pillar.color}18`, color:card.pillar.color, fontFamily:s.monoFont, fontWeight:700, display:'inline-block', marginBottom:14 }}>
                {card.pillar.emoji} {card.pillar.name}
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:s.text, lineHeight:1.5, marginBottom:8 }}>
                {card.type === 'idea' ? card.idea.title : card.prompt}
              </div>
              {card.type === 'idea' && <div style={{ fontSize:10, color:s.dim, fontFamily:s.monoFont }}>From your ideas</div>}
              {card.type === 'prompt' && <div style={{ fontSize:10, color:s.dim, fontFamily:s.monoFont }}>AI suggested</div>}
            </div>
          ))}
        </div>
      )}

      {/* Chosen state */}
      {chosen && (
        <div style={{ padding:'28px 24px', borderRadius:18, background:`${chosen.pillar.color}10`, border:`2px solid ${chosen.pillar.color}`, marginBottom:24 }}>
          <div style={{ fontSize:10, color:chosen.pillar.color, fontFamily:s.monoFont, fontWeight:700, marginBottom:10 }}>
            ✓ You picked · {chosen.pillar.emoji} {chosen.pillar.name}
          </div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, lineHeight:1.5, marginBottom:16 }}>
            {chosen.type === 'idea' ? chosen.idea.title : chosen.prompt}
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => makeTask(chosen)}
              style={{ padding:'8px 18px', borderRadius:8, background:chosen.pillar.color, border:'none', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
              + Add to Tasks
            </button>
            <button onClick={() => { setChosen(null); generatePair(); }}
              style={{ padding:'8px 18px', borderRadius:8, background:'none', border:`1px solid ${s.border}`, color:s.muted, cursor:'pointer', fontSize:12 }}>
              New pair →
            </button>
          </div>
        </div>
      )}

      {/* Neither option */}
      {!chosen && (
        <div style={{ textAlign:'center' }}>
          <button onClick={generatePair}
            style={{ fontSize:11, color:s.dim, background:'none', border:`1px dashed ${s.border}`, borderRadius:8, cursor:'pointer', padding:'8px 20px', fontFamily:s.monoFont }}>
            Neither — give me something else
          </button>
        </div>
      )}

      {/* Recent ideas needing action */}
      {(() => {
        const overdue = pillars.flatMap(p =>
          (ideas[p.id]||[]).filter(i=>i.status==='filming').map(i=>({...i,pillar:p}))
        );
        if (!overdue.length) return null;
        return (
          <div style={{ marginTop:32 }}>
            <div style={{ fontSize:10, letterSpacing:2, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:12 }}>Waiting to be filmed</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {overdue.map(idea => (
                <div key={idea.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:10, background:s.surface, border:`1px solid ${s.border}` }}>
                  <div style={{ fontSize:9, padding:'2px 8px', borderRadius:10, background:`${idea.pillar.color}18`, color:idea.pillar.color, fontFamily:s.monoFont, fontWeight:700, flexShrink:0 }}>
                    {idea.pillar.emoji} {idea.pillar.name}
                  </div>
                  <div style={{ flex:1, fontSize:12, color:s.text }}>{idea.title}</div>
                  <button onClick={() => makeTask(idea)}
                    style={{ fontSize:10, color:s.muted, background:'none', border:`1px solid ${s.border}`, borderRadius:6, cursor:'pointer', padding:'4px 10px', fontFamily:s.monoFont, flexShrink:0 }}>
                    + Task
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Tab IV: Batch + Shot List ─────────────────────────────────
function BatchTab({ data, update, s, artistId, songs }) {
  const batches = data?.batches || [];
  const [showNew, setShowNew]       = useState(false);
  const [viewBatch, setViewBatch]   = useState(null);
  const [batchForm, setBatchForm]   = useState({ date:'', location:'', energy:'medium', songIds:[], dreamMacro:'', notes:'' });
  const [shots, setShots]           = useState([]);
  const [newShot, setNewShot]       = useState('');

  const ENERGY = [
    { id:'low',    label:'Low Energy',    desc:'Phone only. 30 min max.', color:'#1a9080' },
    { id:'medium', label:'Medium Energy', desc:'Camera + 1-2 locations.',  color:'#c98a18' },
    { id:'high',   label:'High Energy',   desc:'Full shoot day. Crew welcome.', color:'#e8621a' },
  ];

  const saveBatch = () => {
    const newBatch = { id: Date.now().toString(), ...batchForm, shots: shots, status:'planned', createdAt: new Date().toISOString() };
    update({ batches: [...batches, newBatch] });
    setShowNew(false);
    setBatchForm({ date:'', location:'', energy:'medium', songIds:[], dreamMacro:'', notes:'' });
    setShots([]);
  };

  const updateBatchShot = (batchId, shotId, changes) => {
    const updated = batches.map(b => b.id === batchId ? {
      ...b, shots: b.shots.map(s => s.id === shotId ? { ...s, ...changes } : s)
    } : b);
    update({ batches: updated });
  };

  const activeBatch = viewBatch ? batches.find(b => b.id === viewBatch) : null;

  return (
    <div style={{ padding:'28px 30px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>Tab IV · Batch + Shot List</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont }}>Shoot Sessions</div>
          <div style={{ fontSize:12, color:s.muted, fontFamily:s.monoFont, marginTop:4 }}>One afternoon = two weeks of content.</div>
        </div>
        <button onClick={() => setShowNew(true)}
          style={{ padding:'8px 18px', borderRadius:10, background:s.accent, border:'none', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
          + New Session
        </button>
      </div>

      {/* New session form */}
      {showNew && (
        <div style={{ marginBottom:24, padding:'24px', borderRadius:16, background:s.surface, border:`1px solid ${s.borderActive}` }}>
          <div style={{ fontSize:13, fontWeight:700, color:s.text, marginBottom:16 }}>Plan a Batch Session</div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Date</label>
              <input type="date" value={batchForm.date} onChange={e => setBatchForm(f=>({...f,date:e.target.value}))}
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Location</label>
              <input value={batchForm.location} onChange={e => setBatchForm(f=>({...f,location:e.target.value}))}
                placeholder="bedroom, studio, outside..."
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
          </div>

          {/* Energy level */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Energy Level</div>
            <div style={{ display:'flex', gap:10 }}>
              {ENERGY.map(e => (
                <div key={e.id} onClick={() => setBatchForm(f=>({...f,energy:e.id}))}
                  style={{ flex:1, padding:'10px 14px', borderRadius:10, background: batchForm.energy===e.id ? `${e.color}18` : s.surface2, border:`1px solid ${batchForm.energy===e.id ? e.color : s.border}`, cursor:'pointer', transition:'all .15s' }}>
                  <div style={{ fontSize:11, fontWeight:700, color: batchForm.energy===e.id ? e.color : s.text, marginBottom:2 }}>{e.label}</div>
                  <div style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont }}>{e.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Songs */}
          {songs.length > 0 && (
            <div className="form-group" style={{ marginBottom:16 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Songs involved</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {songs.map(song => (
                  <div key={song.id} onClick={() => setBatchForm(f => ({ ...f, songIds: f.songIds.includes(song.id) ? f.songIds.filter(id=>id!==song.id) : [...f.songIds, song.id] }))}
                    style={{ padding:'4px 12px', borderRadius:20, background: batchForm.songIds.includes(song.id) ? `${s.accent}22` : s.surface2, border:`1px solid ${batchForm.songIds.includes(song.id) ? s.accent : s.border}`, cursor:'pointer', fontSize:11, color: batchForm.songIds.includes(song.id) ? s.accent : s.muted, transition:'all .15s' }}>
                    {song.title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dream Speak Macro */}
          <div className="form-group" style={{ marginBottom:16 }}>
            <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>
              Dream Speak Macro — <span style={{ fontWeight:400, textTransform:'none' }}>What do you hear? What do you see? First unfiltered perception.</span>
            </label>
            <textarea rows={3} value={batchForm.dreamMacro} onChange={e => setBatchForm(f=>({...f,dreamMacro:e.target.value}))}
              placeholder="Let the subconscious speak..."
              style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8, fontSize:12, lineHeight:1.7, fontStyle:'italic' }}/>
          </div>

          {/* Shot list */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Shot List</div>
            {shots.map((shot, i) => (
              <div key={shot.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <div style={{ fontSize:10, color:s.dim, fontFamily:s.monoFont, width:20, flexShrink:0 }}>#{i+1}</div>
                <div style={{ flex:1, fontSize:12, color:s.text, padding:'6px 10px', background:s.surface2, borderRadius:6 }}>{shot.description}</div>
                <button onClick={() => setShots(shots.filter(x=>x.id!==shot.id))}
                  style={{ background:'none', border:'none', cursor:'pointer', color:s.dim, fontSize:11 }}>✕</button>
              </div>
            ))}
            <div style={{ display:'flex', gap:8 }}>
              <input value={newShot} onChange={e => setNewShot(e.target.value)}
                placeholder="Describe the shot..."
                onKeyDown={e => { if(e.key==='Enter'&&newShot.trim()){ setShots(s=>[...s,{id:Date.now().toString(),description:newShot.trim(),filmed:false}]); setNewShot(''); } }}
                style={{ flex:1, background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8, padding:'8px 12px', fontSize:12 }}/>
              <button onClick={() => { if(newShot.trim()){ setShots(s=>[...s,{id:Date.now().toString(),description:newShot.trim(),filmed:false}]); setNewShot(''); } }}
                style={{ padding:'8px 14px', borderRadius:8, background:s.accent, border:'none', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>+</button>
            </div>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setShowNew(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={saveBatch} disabled={!batchForm.date} className="btn btn-primary">Save Session</button>
          </div>
        </div>
      )}

      {/* Batch list */}
      {batches.length === 0 && !showNew && (
        <div style={{ textAlign:'center', padding:'60px 0', color:s.dim, fontFamily:s.monoFont, fontSize:12 }}>
          No sessions planned yet. One afternoon = two weeks of content.
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {batches.map(batch => {
          const energy = ENERGY.find(e=>e.id===batch.energy)||ENERGY[1];
          const filmed = batch.shots.filter(s=>s.filmed).length;
          const total  = batch.shots.length;
          const songNames = (batch.songIds||[]).map(id => songs.find(s=>s.id===id)?.title).filter(Boolean);
          return (
            <div key={batch.id} style={{ borderRadius:14, background:s.surface, border:`1px solid ${s.border}`, overflow:'hidden' }}>
              <div onClick={() => setViewBatch(viewBatch===batch.id ? null : batch.id)}
                style={{ padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:s.text }}>{batch.date || 'No date'}</div>
                    {batch.location && <div style={{ fontSize:11, color:s.muted, fontFamily:s.monoFont }}>📍 {batch.location}</div>}
                    <div style={{ fontSize:9, padding:'2px 8px', borderRadius:10, background:`${energy.color}18`, color:energy.color, fontFamily:s.monoFont, fontWeight:700 }}>{energy.label}</div>
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {songNames.map(n => <div key={n} style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont }}>🎵 {n}</div>)}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:14, fontWeight:900, color: filmed===total&&total>0 ? '#27ae60' : s.accent, fontFamily:s.monoFont }}>{filmed}/{total}</div>
                  <div style={{ fontSize:9, color:s.dim, fontFamily:s.monoFont }}>shots filmed</div>
                </div>
                <div style={{ fontSize:11, color:s.dim }}>{viewBatch===batch.id ? '▲' : '▼'}</div>
              </div>

              {viewBatch === batch.id && (
                <div style={{ borderTop:`1px solid ${s.border}`, padding:'16px 20px' }}>
                  {batch.dreamMacro && (
                    <div style={{ marginBottom:16, padding:'12px 16px', borderRadius:10, background:s.surface2, border:`1px solid ${s.border}` }}>
                      <div style={{ fontSize:9, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Dream Speak Macro</div>
                      <div style={{ fontSize:12, color:s.muted, lineHeight:1.7, fontStyle:'italic' }}>{batch.dreamMacro}</div>
                    </div>
                  )}
                  <div style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Shot List</div>
                  {batch.shots.map((shot, i) => (
                    <div key={shot.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                      <div onClick={() => updateBatchShot(batch.id, shot.id, {filmed:!shot.filmed})}
                        style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${shot.filmed ? '#27ae60' : s.border}`, background: shot.filmed ? '#27ae60' : 'transparent', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:'#fff' }}>
                        {shot.filmed ? '✓' : ''}
                      </div>
                      <div style={{ fontSize:10, color:s.dim, fontFamily:s.monoFont, width:20, flexShrink:0 }}>#{i+1}</div>
                      <div style={{ flex:1, fontSize:12, color: shot.filmed ? s.muted : s.text, textDecoration: shot.filmed ? 'line-through' : 'none' }}>{shot.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab V: Scripts (absorbed from Scripts.jsx) ────────────────
const PLATFORMS     = ['Instagram Reel','Instagram Story','TikTok','YouTube Short','Caption Only'];
const STATUS_META   = { draft:{label:'Draft',color:'var(--muted)',bg:'var(--surface3)'}, pending:{label:'Pending',color:'var(--amber)',bg:'rgba(201,138,24,.15)'}, approved:{label:'Approved',color:'var(--teal2)',bg:'rgba(26,144,128,.15)'}, posted:{label:'Posted',color:'var(--purple2)',bg:'rgba(123,79,196,.15)'} };
const PLATFORM_ICONS= { 'Instagram Reel':'🎬','Instagram Story':'⭕','TikTok':'♪','YouTube Short':'▶️','Caption Only':'✍️' };

function ScriptsTab({ s, activeArtistId, songs }) {
  const { scripts, loading, addScript, updateScript, deleteScript, submitForApproval, approveScript } = useScripts(activeArtistId);
  const { artists }    = useArtists();
  const { profile, isManager, user } = useAuth();
  const [showModal, setShowModal]    = useState(false);
  const [selected, setSelected]      = useState(null);
  const [aiLoading, setAiLoading]    = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm]              = useState({ title:'', platform:'Instagram Reel', content:'', artistId:activeArtistId||'', linkedSongId:'', aiPrompt:'' });

  const filtered   = scripts.filter(sc => statusFilter==='all'||sc.status===statusFilter);
  const artistName = (id) => artists.find(a=>a.id===id)?.name||'';

  const handleGenerate = async () => {
    if (!form.aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const linkedSong = songs.find(sg=>sg.id===form.linkedSongId);
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ model:'claude-sonnet-4-20250514', max_tokens:1000,
          system:`You are a social media content writer for independent music artists. Write punchy, authentic scripts for ${form.platform}. ${linkedSong?`This is for the song "${linkedSong.title}".`:''} Write ONLY the script. For videos use [VISUAL: description] for shot notes.`,
          messages:[{role:'user',content:form.aiPrompt}] })
      });
      const data = await response.json();
      setForm(f=>({...f, content: data.content?.[0]?.text||''}));
    } catch(e) { console.error(e); }
    setAiLoading(false);
  };

  const handleSave = async () => {
    if (!form.title.trim()||!form.content.trim()) return;
    if (selected) await updateScript(selected.id, { title:form.title, platform:form.platform, content:form.content, linkedSongId:form.linkedSongId });
    else await addScript({ ...form, createdBy:profile?.email });
    setShowModal(false); setSelected(null);
    setForm({ title:'', platform:'Instagram Reel', content:'', artistId:activeArtistId||'', linkedSongId:'', aiPrompt:'' });
  };

  if (loading) return <div style={{ padding:32, color:s.muted }}>Loading...</div>;

  return (
    <div style={{ padding:'28px 30px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>Tab V · Scripts</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont }}>Script Studio</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {['all','draft','pending','approved','posted'].map(st=>(
            <button key={st} onClick={()=>setStatusFilter(st)} className={`btn btn-sm ${statusFilter===st?'btn-primary':'btn-ghost'}`}>
              {st==='all'?'All':STATUS_META[st]?.label}
            </button>
          ))}
          <button onClick={()=>{setSelected(null);setShowModal(true);}} className="btn btn-primary"><Edit3 size={14}/>New Script</button>
        </div>
      </div>

      {isManager&&scripts.filter(sc=>sc.status==='pending').length>0&&(
        <div style={{ padding:'14px 18px', borderRadius:10, marginBottom:20, background:'linear-gradient(90deg,rgba(201,138,24,.12),rgba(232,98,26,.08))', border:'1px solid rgba(201,138,24,.3)', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:13, fontWeight:600 }}>⏳ {scripts.filter(sc=>sc.status==='pending').length} script(s) pending approval</span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
        {filtered.map(script=>{
          const meta=STATUS_META[script.status]||STATUS_META.draft;
          const canEdit=isManager||profile?.artistId===script.artistId;
          return (
            <div key={script.id} className="card fade-up">
              <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>{script.title}</div>
                    <div style={{ fontSize:11 }}>{PLATFORM_ICONS[script.platform]} {script.platform}{artistName(script.artistId)&&` · ${artistName(script.artistId)}`}</div>
                  </div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:20, fontWeight:600, background:meta.bg, color:meta.color, flexShrink:0, fontFamily:'var(--mono)', height:'fit-content' }}>{meta.label}</span>
                </div>
              </div>
              <div style={{ padding:'12px 16px', fontSize:11, color:'var(--muted)', fontFamily:'var(--mono)', lineHeight:1.7, whiteSpace:'pre-wrap', maxHeight:80, overflow:'hidden' }}>{script.content}</div>
              {canEdit&&(
                <div style={{ padding:'10px 16px', borderTop:'1px solid var(--border)', display:'flex', gap:6, flexWrap:'wrap' }}>
                  <button onClick={()=>{setSelected(script);setForm({...script,aiPrompt:''});setShowModal(true);}} className="btn btn-ghost btn-sm">Edit</button>
                  {script.status==='draft'&&<button onClick={()=>submitForApproval(script.id)} className="btn btn-sm" style={{ background:'rgba(201,138,24,.2)',color:'var(--amber)',border:'1px solid rgba(201,138,24,.3)' }}><Send size={11}/>Submit</button>}
                  {isManager&&script.status==='pending'&&<button onClick={()=>approveScript(script.id,user.uid)} className="btn btn-sm" style={{ background:'rgba(26,144,128,.2)',color:'var(--teal2)',border:'1px solid rgba(26,144,128,.3)' }}><Check size={11}/>Approve</button>}
                  {isManager&&script.status==='approved'&&<button onClick={()=>updateScript(script.id,{status:'posted'})} className="btn btn-sm" style={{ background:'rgba(123,79,196,.2)',color:'var(--purple2)',border:'1px solid rgba(123,79,196,.3)' }}>Mark Posted</button>}
                  {isManager&&<button onClick={()=>deleteScript(script.id)} className="btn btn-sm" style={{ background:'rgba(192,57,43,.1)',color:'#e74c3c',border:'1px solid rgba(192,57,43,.3)',marginLeft:'auto' }}>Delete</button>}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length===0&&<div style={{ gridColumn:'1/-1', textAlign:'center', padding:60, color:s.dim, fontFamily:s.monoFont, fontSize:13 }}>No scripts yet</div>}
      </div>

      {showModal&&(
        <Modal onClose={()=>setShowModal(false)} title={selected?'Edit Script':'New Script'}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group" style={{ gridColumn:'1/-1' }}><label>Title</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Cathartic Reel — emotional hook" autoFocus/></div>
            <div className="form-group"><label>Platform</label><select value={form.platform} onChange={e=>setForm(f=>({...f,platform:e.target.value}))}>{PLATFORMS.map(p=><option key={p}>{p}</option>)}</select></div>
            <div className="form-group"><label>Linked Song</label><select value={form.linkedSongId} onChange={e=>setForm(f=>({...f,linkedSongId:e.target.value}))}><option value="">None</option>{songs.map(sg=><option key={sg.id} value={sg.id}>{sg.title}</option>)}</select></div>
          </div>
          <div style={{ padding:'14px 16px', borderRadius:10, background:'linear-gradient(135deg,rgba(123,79,196,.1),rgba(232,98,26,.06))', border:'1px solid var(--border-active)', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}><Sparkles size={14} style={{ color:'var(--purple2)' }}/><span style={{ fontSize:12, fontWeight:700, color:'var(--purple2)' }}>AI Script Generator</span></div>
            <textarea rows={2} value={form.aiPrompt} onChange={e=>setForm(f=>({...f,aiPrompt:e.target.value}))} placeholder="Describe what you want..." style={{ marginBottom:10 }}/>
            <button onClick={handleGenerate} disabled={aiLoading||!form.aiPrompt.trim()} className="btn btn-primary btn-sm">{aiLoading?'Generating...':<><Sparkles size={13}/>Generate</>}</button>
          </div>
          <div className="form-group"><label>Script Content</label><textarea rows={10} value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))} placeholder="Write or generate above..." style={{ fontFamily:'var(--mono)', fontSize:12, lineHeight:1.8 }}/></div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={()=>setShowModal(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={handleSave} className="btn btn-primary">{selected?'Save Changes':'Save Script'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab VI: Schedule ──────────────────────────────────────────
const POST_PLATFORMS = ['Instagram Reel','Instagram Story','TikTok','YouTube Short','Caption Only'];
const POST_STATUSES  = [
  { id:'idea',      label:'Idea',      color:'var(--dim)' },
  { id:'filmed',    label:'Filmed',    color:'#7b4fc4' },
  { id:'editing',   label:'Editing',   color:'#1a9080' },
  { id:'scheduled', label:'Scheduled', color:'#e8621a' },
  { id:'posted',    label:'Posted',    color:'#27ae60' },
];

function ScheduleTab({ data, update, s, artistId, songs, onCreateTask }) {
  const pillars  = data?.pillars || PILLARS[artistId] || PILLARS.avidx;
  const ideas    = data?.ideas   || {};
  const schedule = data?.schedule || [];
  const [showAdd,            setShowAdd]           = useState(false);
  const [showScheduledQueue, setShowScheduledQueue] = useState(false);
  const [viewPost,           setViewPost]           = useState(null);
  const [postForm,           setPostForm]           = useState({ date:'', platform:'Instagram Reel', pillarId:'', osc:'', ofsc:'', songId:'', status:'idea', assignedTo:'', notes:'' });
  const [weekOffset,         setWeekOffset]         = useState(0);

  // Collect all ideas marked "scheduled" from all pillars + unsorted
  const scheduledIdeas = [...pillars, { id:'unsorted', name:'Unsorted', color:s.muted, emoji:'📥' }]
    .flatMap(p => (ideas[p.id]||[]).filter(i => i.status==='scheduled').map(i => ({ ...i, pillar:p })));

  // Import an idea card into the post form
  const importIdea = (idea) => {
    setPostForm(f => ({
      ...f,
      osc:      idea.hook     || '',
      ofsc:     idea.caption  || '',
      pillarId: idea.pillar?.id !== 'unsorted' ? idea.pillar?.id : '',
      platform: idea.format === 'Story' ? 'Instagram Story' : idea.format === 'Carousel' ? 'Instagram Reel' : f.platform,
    }));
    setShowScheduledQueue(false);
    setShowAdd(true);
  };

  // Get week dates
  const getWeekDates = (offset=0) => {
    const today = new Date();
    const day   = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - day + 1 + (offset*7));
    return Array.from({length:7},(_,i)=>{ const d=new Date(monday); d.setDate(monday.getDate()+i); return d; });
  };
  const weekDates = getWeekDates(weekOffset);
  const fmt = (d) => d.toISOString().split('T')[0];
  const dayLabel = (d) => d.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'});

  const savePost = () => {
    const newPost = { id: Date.now().toString(), ...postForm, createdAt: new Date().toISOString() };
    update({ schedule: [...schedule, newPost] });
    setShowAdd(false);
    setPostForm({ date:'', platform:'Instagram Reel', pillarId:'', osc:'', ofsc:'', songId:'', status:'idea', assignedTo:'', notes:'' });
  };

  const updatePost = (postId, changes) => {
    update({ schedule: schedule.map(p => p.id===postId ? {...p,...changes} : p) });
  };

  const deletePost = (postId) => {
    update({ schedule: schedule.filter(p => p.id!==postId) });
  };

  const taskFromPost = (post) => {
    const pillar = pillars.find(p=>p.id===post.pillarId);
    onCreateTask({
      title: `[Content] ${post.osc || post.platform} — ${pillar?.name||''}`,
      category:'content', priority:'P1', artistId,
      assignedTo: post.assignedTo || artistId,
      dueDate: post.date,
    });
  };

  const editingPost = viewPost ? schedule.find(p=>p.id===viewPost) : null;

  return (
    <div style={{ padding:'28px 30px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>Tab VI · Schedule</div>
          <div style={{ fontSize:18, fontWeight:700, color:s.text, fontFamily:s.titleFont }}>Content Schedule</div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <button onClick={()=>setWeekOffset(w=>w-1)} style={{ background:'none', border:`1px solid ${s.border}`, color:s.muted, borderRadius:8, cursor:'pointer', padding:'6px 12px', fontSize:12 }}>←</button>
          <button onClick={()=>setWeekOffset(0)} style={{ background:'none', border:`1px solid ${s.border}`, color:s.muted, borderRadius:8, cursor:'pointer', padding:'6px 12px', fontSize:11, fontFamily:s.monoFont }}>Today</button>
          <button onClick={()=>setWeekOffset(w=>w+1)} style={{ background:'none', border:`1px solid ${s.border}`, color:s.muted, borderRadius:8, cursor:'pointer', padding:'6px 12px', fontSize:12 }}>→</button>
          {/* Scheduled queue button */}
          <button onClick={() => setShowScheduledQueue(v=>!v)}
            style={{ padding:'8px 14px', borderRadius:10, background: showScheduledQueue ? `${s.accent}18` : 'none', border:`1px solid ${showScheduledQueue ? s.accent : s.border}`, color: showScheduledQueue ? s.accent : s.muted, fontWeight:600, cursor:'pointer', fontSize:11, fontFamily:s.monoFont, display:'flex', alignItems:'center', gap:6 }}>
            {scheduledIdeas.length > 0 && <span style={{ background:s.accent, color:'#fff', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800 }}>{scheduledIdeas.length}</span>}
            Ready to Post {showScheduledQueue ? '▲' : '▼'}
          </button>
          <button onClick={()=>{setShowAdd(true);setShowScheduledQueue(false);}} style={{ padding:'8px 18px', borderRadius:10, background:s.accent, border:'none', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>+ Add Post</button>
        </div>
      </div>

      {/* Scheduled ideas queue */}
      {showScheduledQueue && (
        <div style={{ marginBottom:20, padding:'16px 20px', borderRadius:14, background:s.surface, border:`1px solid ${s.accent}44` }}>
          <div style={{ fontSize:10, letterSpacing:2, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:12 }}>
            Ready to Post — click any card to import into schedule
          </div>
          {scheduledIdeas.length === 0 ? (
            <div style={{ fontSize:12, color:s.dim, fontFamily:s.monoFont }}>No ideas marked "Scheduled" yet. Mark ideas in Tab II to see them here.</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:10 }}>
              {scheduledIdeas.map(idea => (
                <div key={idea.id} onClick={() => importIdea(idea)}
                  style={{ padding:'12px 14px', borderRadius:10, background:s.surface2, border:`1px solid ${idea.pillar?.color||s.border}33`, cursor:'pointer', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=idea.pillar?.color||s.accent; e.currentTarget.style.transform='translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=`${idea.pillar?.color||s.border}33`; e.currentTarget.style.transform='translateY(0)'; }}>
                  <div style={{ fontSize:9, color:idea.pillar?.color||s.muted, fontFamily:s.monoFont, fontWeight:700, marginBottom:6 }}>
                    {idea.pillar?.emoji} {idea.pillar?.name} {idea.format && `· ${idea.format}`}
                  </div>
                  {idea.hook && <div style={{ fontSize:12, fontWeight:700, color:s.text, marginBottom:4, lineHeight:1.4 }}>{idea.hook}</div>}
                  {idea.caption && <div style={{ fontSize:11, color:s.muted, lineHeight:1.5, fontStyle:'italic' }}>{idea.caption}</div>}
                  <div style={{ fontSize:9, color:s.accent, fontFamily:s.monoFont, marginTop:8, fontWeight:600 }}>↗ Import to schedule</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Week grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8, marginBottom:24 }}>
        {weekDates.map(date => {
          const dateStr = fmt(date);
          const isToday = dateStr === fmt(new Date());
          const dayPosts = schedule.filter(p=>p.date===dateStr);
          return (
            <div key={dateStr} style={{ minHeight:120, borderRadius:12, background: isToday ? `${s.accent}08` : s.surface, border:`1px solid ${isToday ? s.accent : s.border}`, padding:'10px 10px' }}>
              <div style={{ fontSize:10, fontWeight: isToday ? 800 : 600, color: isToday ? s.accent : s.muted, fontFamily:s.monoFont, marginBottom:8 }}>
                {dayLabel(date)}
              </div>
              {dayPosts.map(post => {
                const st     = POST_STATUSES.find(x=>x.id===post.status)||POST_STATUSES[0];
                const pillar = pillars.find(p=>p.id===post.pillarId);
                return (
                  <div key={post.id} onClick={() => setViewPost(post.id)}
                    style={{ marginBottom:6, padding:'6px 8px', borderRadius:8, background:s.surface2, border:`1px solid ${pillar?.color||s.border}22`, cursor:'pointer' }}>
                    <div style={{ fontSize:9, color:pillar?.color||s.muted, fontFamily:s.monoFont, fontWeight:700, marginBottom:2 }}>{PLATFORM_ICONS[post.platform]} {pillar?.name}</div>
                    <div style={{ fontSize:10, color:s.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{post.osc||'No hook yet'}</div>
                    <div style={{ fontSize:8, color:st.color, fontFamily:s.monoFont, marginTop:2, fontWeight:700 }}>{st.label}</div>
                  </div>
                );
              })}
              <button onClick={() => { setShowAdd(true); setPostForm(f=>({...f,date:dateStr})); }}
                style={{ width:'100%', background:'none', border:`1px dashed ${s.border}`, borderRadius:6, cursor:'pointer', color:s.dim, fontSize:10, padding:'4px', fontFamily:s.monoFont, marginTop:4 }}>
                +
              </button>
            </div>
          );
        })}
      </div>

      {/* Add post form */}
      {showAdd && (
        <div style={{ marginBottom:24, padding:'24px', borderRadius:16, background:s.surface, border:`1px solid ${s.borderActive}` }}>
          <div style={{ fontSize:13, fontWeight:700, color:s.text, marginBottom:16 }}>New Post</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Date</label>
              <input type="date" value={postForm.date} onChange={e=>setPostForm(f=>({...f,date:e.target.value}))}
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Platform</label>
              <select value={postForm.platform} onChange={e=>setPostForm(f=>({...f,platform:e.target.value}))}
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}>
                {POST_PLATFORMS.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Pillar</label>
              <select value={postForm.pillarId} onChange={e=>setPostForm(f=>({...f,pillarId:e.target.value}))}
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}>
                <option value="">Select pillar</option>
                {pillars.map(p=><option key={p.id} value={p.id}>{p.emoji} {p.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>OSC — On Screen Caption</label>
              <input value={postForm.osc} onChange={e=>setPostForm(f=>({...f,osc:e.target.value}))} placeholder="The hook. Stops the scroll."
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>OfSC — Caption</label>
              <input value={postForm.ofsc} onChange={e=>setPostForm(f=>({...f,ofsc:e.target.value}))} placeholder="The context, the emotion, the CTA."
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Linked Song</label>
              <select value={postForm.songId} onChange={e=>setPostForm(f=>({...f,songId:e.target.value}))}
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}>
                <option value="">None</option>
                {songs.map(sg=><option key={sg.id} value={sg.id}>{sg.title}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin:0 }}>
              <label style={{ fontSize:10, color:s.muted, fontFamily:s.monoFont, textTransform:'uppercase', letterSpacing:1 }}>Assign to</label>
              <input value={postForm.assignedTo} onChange={e=>setPostForm(f=>({...f,assignedTo:e.target.value}))} placeholder="avidx, ahzira, jay..."
                style={{ background:s.surface2, border:`1px solid ${s.border}`, color:s.text, borderRadius:8 }}/>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'space-between' }}>
            <button onClick={() => taskFromPost(postForm)} style={{ padding:'8px 14px', borderRadius:8, background:'none', border:`1px solid ${s.border}`, color:s.muted, cursor:'pointer', fontSize:12 }}>
              + Create as Task
            </button>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setShowAdd(false)} className="btn btn-ghost">Cancel</button>
              <button onClick={savePost} disabled={!postForm.date} className="btn btn-primary">Add to Schedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Post detail */}
      {editingPost && (
        <div style={{ position:'fixed', inset:0, zIndex:999, background:'rgba(0,0,0,.8)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={()=>setViewPost(null)}>
          <div style={{ background:s.surface, border:`1px solid ${s.border}`, borderRadius:16, padding:28, maxWidth:500, width:'100%' }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:700, color:s.text }}>{PLATFORM_ICONS[editingPost.platform]} {editingPost.platform}</div>
              <button onClick={()=>setViewPost(null)} style={{ background:'none', border:'none', cursor:'pointer', color:s.dim, fontSize:16 }}>✕</button>
            </div>
            <div style={{ fontSize:12, color:s.muted, fontFamily:s.monoFont, marginBottom:4 }}>OSC</div>
            <div style={{ fontSize:14, fontWeight:700, color:s.text, marginBottom:12 }}>{editingPost.osc||'—'}</div>
            <div style={{ fontSize:12, color:s.muted, fontFamily:s.monoFont, marginBottom:4 }}>Caption</div>
            <div style={{ fontSize:12, color:s.muted, lineHeight:1.7, marginBottom:16 }}>{editingPost.ofsc||'—'}</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {POST_STATUSES.map(st => (
                <button key={st.id} onClick={()=>updatePost(editingPost.id,{status:st.id})}
                  style={{ padding:'4px 12px', borderRadius:20, background: editingPost.status===st.id ? `${st.color}22` : s.surface2, border:`1px solid ${editingPost.status===st.id ? st.color : s.border}`, color: editingPost.status===st.id ? st.color : s.muted, fontSize:10, cursor:'pointer', fontFamily:s.monoFont, fontWeight:700 }}>
                  {st.label}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
              <button onClick={()=>{taskFromPost(editingPost);setViewPost(null);}}
                style={{ padding:'6px 14px', borderRadius:8, background:'none', border:`1px solid ${s.border}`, color:s.muted, cursor:'pointer', fontSize:12 }}>
                + Task
              </button>
              <button onClick={()=>{deletePost(editingPost.id);setViewPost(null);}}
                style={{ padding:'6px 14px', borderRadius:8, background:'rgba(192,57,43,.1)', border:'1px solid rgba(192,57,43,.3)', color:'#e74c3c', cursor:'pointer', fontSize:12 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab definitions ───────────────────────────────────────────
const TABS = [
  { id:'identity', roman:'I',   label:'Identity',       icon:'🜃' },
  { id:'pillars',  roman:'II',  label:'Pillars',        icon:'⚡' },
  { id:'spark',    roman:'III', label:'Spark',          icon:'✦' },
  { id:'batch',    roman:'IV',  label:'Batch',          icon:'🎬' },
  { id:'scripts',  roman:'V',   label:'Scripts',        icon:'✍️' },
  { id:'schedule', roman:'VI',  label:'Schedule',       icon:'📅' },
];

// ── Main Content Command Center ───────────────────────────────
export default function Content({ activeArtistId }) {
  const { profile } = useAuth();
  const { songs }   = useSongs(activeArtistId);
  const [activeTab, setActiveTab] = useState('identity');

  const ownArtistId = profile?.email === 'aiahzira@gmail.com' ? 'ahzira' : 'avidx';
  const resolvedId  = (() => {
    if (!activeArtistId) return ownArtistId;
    if (activeArtistId === 'ahzira') return 'ahzira';
    if (activeArtistId === 'avidx') return 'avidx';
    return ownArtistId;
  })();

  const s = SKINS[resolvedId] || SKINS.avidx;
  const { data, update } = useContentData(resolvedId);

  // Create a task from content — saves to Firestore tasks collection
  const onCreateTask = async (taskData) => {
    try {
      await addDoc(collection(db, 'tasks'), {
        ...taskData,
        status: 'todo',
        createdBy: profile?.email || '',
        createdAt: serverTimestamp(),
      });
    } catch(e) { console.error('create task:', e); }
  };

  if (!data) return (
    <div style={{ padding:32, color:'var(--muted)', fontFamily:'var(--mono)', fontSize:12, letterSpacing:2 }}>
      LOADING CONTENT SYSTEM...
    </div>
  );

  return (
    <div style={{ position:'relative', zIndex:1, minHeight:'100vh', background:s.bg, transition:'background .4s' }}>
      <SkinBackground skin={resolvedId} />

      {/* ── Header ── */}
      <div style={{ padding:'28px 30px 0', borderBottom:`1px solid ${s.border}`, position:'relative', zIndex:1 }}>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:s.muted, fontFamily:s.monoFont, marginBottom:6 }}>
            Content Command Center
          </div>
          <h1 style={{ fontSize:26, fontWeight:900, color:s.text, fontFamily:s.titleFont, marginBottom:4, letterSpacing:-0.5 }}>
            {s.label}
          </h1>
        </div>

        {/* Tab bar */}
        <div style={{ display:'flex', gap:0, overflowX:'auto' }}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'10px 20px', border:'none', cursor:'pointer', whiteSpace:'nowrap',
                background: isActive ? s.surface : 'transparent',
                borderTop: `2px solid ${isActive ? s.tabAccent : 'transparent'}`,
                borderBottom: `1px solid ${isActive ? s.surface : 'transparent'}`,
                marginBottom: isActive ? -1 : 0,
                transition:'all .15s',
                color: isActive ? s.text : s.muted,
              }}>
                <div style={{
                  width:22, height:22, borderRadius:'50%', flexShrink:0,
                  background: isActive ? s.tabAccent : s.surface2,
                  border:`1px solid ${isActive ? s.tabAccent : s.border}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:9, fontWeight:900, fontFamily:s.monoFont,
                  color: isActive ? '#fff' : s.dim, transition:'all .15s',
                }}>{tab.roman}</div>
                <span style={{ fontSize:12, fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div style={{ position:'relative', zIndex:1 }}>
        {activeTab === 'identity' && <IdentityTab data={data} update={update} s={s} artistId={resolvedId} />}
        {activeTab === 'pillars'  && <PillarsTab  data={data} update={update} s={s} artistId={resolvedId} />}
        {activeTab === 'spark'    && <SparkTab    data={data} update={update} s={s} artistId={resolvedId} onCreateTask={onCreateTask} />}
        {activeTab === 'batch'    && <BatchTab    data={data} update={update} s={s} artistId={resolvedId} songs={songs} />}
        {activeTab === 'scripts'  && <ScriptsTab  s={s} activeArtistId={resolvedId} songs={songs} />}
        {activeTab === 'schedule' && <ScheduleTab data={data} update={update} s={s} artistId={resolvedId} songs={songs} onCreateTask={onCreateTask} />}
      </div>
    </div>
  );
}
